import { cloneDeep } from 'lodash'
import { DashboardWidget } from 'features/widget/types'
import { InsightCount } from 'schemas/action/InsightCountSchema'
import { InsightData } from 'schemas/action/InsightDataSchema'
import GroupMetric, { GroupMetricStrings } from 'enums/GroupMetric'
import { GroupMetricResult } from 'schemas/GroupMetricResultSchema'
import SocialWebChannelType, { SocialWebChannelTypeStrings } from 'enums/SocialWebChannelType'
import i18n from 'i18n'
import { SocialWebChannel } from 'schemas/SocialWebChannelSchema'
import { MetricValues, TableRow } from './types'
import {
  SHARES_METRICS_SUPPORTED_CHANNELS,
  SHARES_METRICS,
  VIEWS_METRICS_SUPPORTED_CHANNELS,
  VIEWS_METRICS,
  METRICS_WITH_TOTAL,
  groupTitleTranslations
} from './constants'

export const retrieveInsightDataSet = (widgetData: DashboardWidget<InsightCount>) =>
  widgetData?.data?.[0]?.results?.[0]?.data?.insights?.[0]?.dataset?.[0]?.set || []

export const filterOutZeroBrandPosts = (insightDataSet: InsightData[]) =>
  insightDataSet.filter((insightData) => {
    const { groupMetricResults } = insightData
    const brandPosts = groupMetricResults?.find((metric) => metric.type === GroupMetric.BRAND_POSTS)
    return brandPosts?.value !== 0
  })

const generateUnsupportedMetrics = (
  channelType: SocialWebChannelTypeStrings
): GroupMetricStrings[] => {
  const unsupportedMetrics = [] as GroupMetricStrings[]
  if (!SHARES_METRICS_SUPPORTED_CHANNELS.includes(channelType)) {
    unsupportedMetrics.push(...SHARES_METRICS)
  }
  if (!VIEWS_METRICS_SUPPORTED_CHANNELS.includes(channelType)) {
    unsupportedMetrics.push(...VIEWS_METRICS)
  }
  return unsupportedMetrics
}

const overwriteUnsupportedMetricValues = (
  metrics: GroupMetricResult[],
  unsupportedMetrics: GroupMetricStrings[]
) =>
  metrics.map((currentMetric) => {
    if (unsupportedMetrics.includes(currentMetric.type!)) {
      return {
        ...currentMetric,
        value: undefined
      }
    }
    return {
      ...currentMetric
    }
  })

export const overwriteGroupMetricResults = (insightDataSet: InsightData[]) =>
  insightDataSet.map((currentSet) => {
    const clonedCurrentSet = cloneDeep(currentSet)
    const currentChannelType = clonedCurrentSet.channelInfo!.type!

    const unsupportedMettrics = generateUnsupportedMetrics(currentChannelType)

    return {
      ...clonedCurrentSet,
      groupMetricResults: overwriteUnsupportedMetricValues(
        clonedCurrentSet.groupMetricResults!,
        unsupportedMettrics
      )
    }
  })

const generateMetricValues = (insightData: InsightData): MetricValues => {
  const { groupMetricResults } = insightData
  return (
    groupMetricResults?.reduce<MetricValues>((accumulator, currentMetric) => {
      if (currentMetric.type) {
        return {
          ...accumulator,
          [currentMetric.type]: currentMetric.value
        }
      }
      return accumulator
    }, {} as MetricValues) ?? ({} as MetricValues)
  )
}

const getChannelAuthor = (insightData: InsightData) => {
  const { channelInfo = {} } = insightData
  const { type, name, channelId, inputValue: userHandle } = channelInfo as SocialWebChannel

  let author = name
  if (type === SocialWebChannelType.YOUTUBE) {
    author = channelId || undefined
  } else if (type === SocialWebChannelType.TIKTOK) {
    author = userHandle
  }
  return author
}

export const convertToTableRowData = (insightDataSet: InsightData[]): TableRow[] =>
  insightDataSet.map((insightData) => {
    const { channelInfo, image } = insightData
    return {
      channelId: channelInfo?.channelId || '',
      channelType: channelInfo?.type || SocialWebChannelType.OTHER,
      channelName: channelInfo?.name || '',
      channelAuthor: getChannelAuthor(insightData),
      channelLevel: channelInfo?.level || undefined,
      image,
      ...generateMetricValues(insightData)
    }
  })

const generateMetricSum = (rowData: TableRow[], metric: GroupMetricStrings) => {
  if (rowData.every((currentRowData) => currentRowData[metric] === undefined)) {
    return undefined
  }
  return rowData.reduce((acc, currentRowData) => {
    const currentValue = currentRowData[metric]
    if (currentValue === undefined || currentValue! < 0) {
      return acc
    }
    return acc + currentValue!
  }, 0)
}

const generateMetricSums = (rowData: TableRow[], metrics: GroupMetricStrings[]) =>
  metrics.reduce(
    (accumulator, currentMetric) => ({
      ...accumulator,
      [currentMetric]: generateMetricSum(rowData, currentMetric)
    }),
    {} as { [key in GroupMetricStrings]?: number }
  )

const generateMetricTotals = (rowData: TableRow[], metrics: GroupMetricStrings[]) => {
  const metricSums = generateMetricSums(rowData, metrics)
  return metrics.reduce(
    (accumulator, currentMetric) => ({
      ...accumulator,
      [currentMetric]: METRICS_WITH_TOTAL.includes(currentMetric)
        ? metricSums[currentMetric]
        : undefined
    }),
    {} as { [key in GroupMetricStrings]?: number }
  )
}

const generateMetricAverages = (rowData: TableRow[], metrics: GroupMetricStrings[]) => {
  const metricSums = generateMetricSums(rowData, metrics)
  return metrics.reduce(
    (accumulator, currentMetric) => ({
      ...accumulator,
      [currentMetric]:
        metricSums[currentMetric] === undefined
          ? undefined
          : metricSums[currentMetric]! / rowData.length
    }),
    {} as { [key in GroupMetricStrings]?: number }
  )
}

export const generateMetricsAggregations = (rowData: TableRow[], metrics: GroupMetricStrings[]) => {
  const metricTotals = generateMetricTotals(rowData, metrics)
  const metricAverages = generateMetricAverages(rowData, metrics)
  return {
    metricTotals,
    metricAverages
  }
}

export const generateGroupedRowDataByNetworkType = (rowData: TableRow[]) => {
  const groupedData = rowData.reduce<{ [key: string]: TableRow[] }>((accumulator, currentRow) => {
    const { channelType } = currentRow
    if (!accumulator[channelType]) {
      accumulator[channelType] = []
    }
    accumulator[channelType].push(currentRow)
    return accumulator
  }, {})
  return Object.values(groupedData)
}

export const getGroupTitles = (rowData: TableRow[]) =>
  Array.from(new Set(rowData.map((row) => row.channelType))).map((channelType) =>
    i18n.t(groupTitleTranslations[channelType])
  )
